import { useMemo } from "react";
import { Link } from "react-router-dom";
import { api, type Tx } from "../api";
import { ageLabel, ageMs, cached, connection } from "../observation";
import { useObservation, useStats } from "../observations";
import Stat from "../components/Stat";
import TxRow from "../components/TxRow";

// kind=1 is a witness record, kind=4 a snapshot — both are produced by the
// protocol every round and are tentative by design (only transfers finalize
// quickly). We separate these "system" records from member transfers so the
// counts don't read as a backlog of stuck payments.
const SYSTEM_KINDS = new Set([1, 4]);
const isSystemTx = (t: Tx) => SYSTEM_KINDS.has(t.kind);

const readRecent = (signal: AbortSignal) => api.recent(undefined, signal);

export default function HomePage() {
  const observation = useStats();
  const stats = observation.data;
  const health = connection(observation);
  const recent = useObservation(readRecent);
  const txs = recent.data?.txs ?? [];
  const witnesses = recent.data?.witnesses ?? [];

  const rounds = witnesses;
  // Member transfers (kind=0 and other non-system kinds) vs the protocol's
  // own witness/snapshot bookkeeping.
  const transfers = useMemo(() => txs.filter((t) => !isSystemTx(t)), [txs]);

  // Of the protocol's total tx count, the witnesses (kind=1) are tentative by
  // design — so "pending" is mostly the mesh's own bookkeeping, not stuck
  // payments. Surface a transfers-only finality story alongside the raw total.
  const transferPending =
    stats != null
      ? Math.max(
          0,
          stats.txs_total -
            stats.txs_final -
            stats.txs_invalid -
            stats.witnesses_total,
        )
      : 0;

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold text-mesh-accent">Live Mesh State</h1>
      <p data-testid="home-health" className="text-sm text-mesh-mute">
        {health.label} · {stats ? `last successful refresh ${ageLabel(ageMs(observation))}` : "waiting for an API observation"}
        {cached(observation) && " · cached data, not current connection proof"}
      </p>
      {(recent.error || cached(recent)) && (
        <div role="alert" className="bg-mesh-warn/20 text-mesh-warn p-3 rounded text-sm">
          Recent records {recent.data ? `cached · refreshed ${ageLabel(ageMs(recent))}` : "unavailable"}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 [&_.text-2xl]:break-words">
        <Stat
          label="latest round"
          value={stats?.latest_round ?? "…"}
          hint={
            stats?.latest_round_phys_time
              ? new Date(stats.latest_round_phys_time * 1000).toLocaleTimeString()
              : undefined
          }
          tone={health.tone}
        />
        <Stat
          label="tx/sec (1m)"
          value={stats ? stats.tx_per_sec_1m.toFixed(2) : "…"}
          hint={
            stats ? `${stats.tx_per_sec_5m.toFixed(2)} 5m avg` : undefined
          }
        />
        <Stat
          label="active accounts (24h)"
          value={stats?.accounts_active_24h ?? "…"}
        />
        <Stat
          label="updated"
          value={stats ? ageLabel(stats.indexer_lag_ms + ageMs(observation)) : "…"}
          hint="reported indexer lag plus elapsed observation age"
          tone={health.tone}
        />
        <Stat
          label="transfers"
          value={stats ? Math.max(0, stats.txs_total - stats.witnesses_total) : "…"}
          hint={
            stats
              ? `${stats.txs_final} final · ${transferPending} settling · ${stats.txs_invalid} invalid`
              : undefined
          }
        />
        <Stat
          label="witness records"
          value={stats?.witnesses_total ?? "…"}
          hint="tentative by design — one per round"
        />
        <Stat
          label="node"
          value={health.label}
          hint={stats ? `last reported ${stats.node_connected ? "connected" : "disconnected"}` : "not yet observed"}
          tone={health.tone}
        />
        <Stat
          label="last event"
          value={
            stats?.last_event_at
              ? new Date(stats.last_event_at).toLocaleTimeString()
              : "—"
          }
        />
      </div>

      <p className="text-xs text-mesh-mute -mt-2">
        The mesh records a “witness” every round to keep the ledger honest;
        these stay <span className="text-mesh-warn">tentative</span> by design.
        Member <span className="text-mesh-accent">transfers</span> finalize in a
        round or two — those are the numbers under “transfers”.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="lg:col-span-2 bg-mesh-panel/40 rounded-lg p-4">
          <h2 className="text-sm uppercase tracking-wider text-mesh-mute mb-3">
            recent transfers
          </h2>
          <div>
            {transfers.length === 0 && (
              <div className="text-mesh-mute text-sm py-6 text-center">
                {recent.data ? "no transfers in the recent window" : recent.error ? "recent transfers unavailable" : "checking recent transfers…"}
              </div>
            )}
            {transfers.map((t) => (
              <TxRow key={t.tx_id} tx={t} />
            ))}
          </div>
        </section>
        <section className="bg-mesh-panel/40 rounded-lg p-4">
          <h2 className="text-sm uppercase tracking-wider text-mesh-mute mb-3">
            witness rounds
          </h2>
          <div className="space-y-2">
            {rounds.length === 0 && (
              <div className="text-mesh-mute text-sm py-6 text-center">
                {recent.data ? "no witnesses in the recent window" : recent.error ? "witness rounds unavailable" : "checking witness rounds…"}
              </div>
            )}
            {rounds.map((w) => (
              <Link
                key={w.tx_id}
                to={w.round == null ? `/tx/${w.tx_id}` : `/round/${w.round}`}
                className="block p-2 rounded bg-mesh-bg hover:bg-mesh-panel/60 text-sm"
              >
                <div className="flex justify-between">
                  <span className="font-bold text-mesh-accent">
                    {w.round == null ? "round not indexed" : `round ${w.round}`}
                  </span>
                  {w.phys_time != null && (
                    <span className="text-xs text-mesh-mute">
                      {new Date(w.phys_time * 1000).toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div className="hex text-xs text-mesh-mute truncate" title={w.witness_pubkey ?? undefined}>
                  {w.witness_pubkey ? `by ${w.witness_pubkey.slice(0, 12)}…` : "witness key not indexed"}
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
